import { useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import React, { useEffect, useRef } from 'react'

import { AppSidebar } from '@/components/app-sidebar'
import { FullPageSpinnerPortal } from '@/components/common/Spinner/FullPagePortal'
import { DashboardNavigationBar } from '@/components/layouts/dashboard'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { AdminNavItems } from '@/config/nav'
import { useIsMobile } from '@/hooks/useIsMobile'
import { getApiAuthMeOptions } from '@/lib/api/client/@tanstack/react-query.gen'

export const Route = createFileRoute('/_liff/admin')({
  component: RouteComponent,
})

const routeLabels: Record<string, string> = {
  '/admin': 'แดชบอร์ด',
  '/admin/guests': 'รายชื่อแขก',
  '/admin/guests/add': 'เพิ่มรายชื่อแขก',
  '/admin/guests/master/add': 'เพิ่มรายชื่อแขกใหม่',
  '/admin/guests/master/edit': 'แก้ไขรายชื่อแขก',
  '/admin/tables': 'จัดการโต๊ะ',
  '/admin/blessings': 'คำอวยพร',
  '/admin/angpao': 'อั่งเปา',
  '/admin/wall': 'จอภาพของแขก',
  '/admin/random-number': 'สุ่มหมายเลข',
  '/admin/settings': 'ตั้งค่า',
  '/admin/users': 'ผู้ดูแลระบบ',
  '/admin/richmenu': 'จัดการ Rich Menu',
  '/admin/richmenu/create': 'สร้าง Rich Menu',
  '/random-number-display': 'หน้าจอแสดงผลสุ่มหมายเลข',
}

function RouteComponent() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isMobile = useIsMobile()
  const scrollRef = useRef<HTMLDivElement>(null)
  const { data, isLoading } = useQuery(getApiAuthMeOptions())

  const allowedModules = data?.profile?.allowedModules ?? null
  const isSuperAdmin = !isLoading && !!data && allowedModules === null

  const visibleNavItems = AdminNavItems.filter((item) => {
    if (item.superAdminOnly) return isSuperAdmin
    if (!item.module) return true
    return isSuperAdmin || (allowedModules ?? []).includes(item.module)
  })

  const pathSegments = pathname.split('/').filter(Boolean)
  const breadcrumbs = [
    // { label: routeLabels['/admin'], href: '/admin' }
  ] as Array<{ label: string; href: string }>

  let currentPath = ''
  pathSegments.forEach((segment) => {
    currentPath += `/${segment}`
    if (
      routeLabels[currentPath] &&
      currentPath !== '/' &&
      currentPath !== '/admin'
    ) {
      breadcrumbs.push({
        label: routeLabels[currentPath],
        href: currentPath,
      })
    }
  })

  useEffect(() => {
    if (isLoading || !data) return

    if (data.profile?.role !== 'admin') {
      navigate({ to: '/landing' })
      return
    }

    const modules = data.profile?.allowedModules ?? null
    const segments = pathname.split('/').filter(Boolean)
    const moduleKey = segments[1] ?? null // segment after /admin/

    if (!moduleKey) return

    if (moduleKey === 'users' && modules !== null) {
      navigate({ to: '/admin' })
      return
    }

    if (
      moduleKey !== 'users' &&
      modules !== null &&
      !modules.includes(moduleKey)
    ) {
      navigate({ to: '/admin' })
    }
  }, [data, isLoading, navigate, pathname])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
  }, [pathname])

  if (isLoading) {
    return <FullPageSpinnerPortal />
  }

  if (data?.profile?.role !== 'admin') {
    return null
  }

  if (isMobile) {
    return (
      <div className="flex h-dvh flex-1 flex-col">
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-hidden overflow-y-auto"
        >
          <Outlet />
        </div>
        <DashboardNavigationBar items={visibleNavItems} />
      </div>
    )
  }

  return (
    <SidebarProvider>
      <AppSidebar allowedModules={allowedModules} />
      <SidebarInset
        ref={scrollRef}
        className="overflow-y-auto bg-white"
      >
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator
              orientation="vertical"
              className="mr-2 data-[orientation=vertical]:h-7"
            />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={crumb.href}>
                    <BreadcrumbItem
                      className={
                        index < breadcrumbs.length - 1 ? 'hidden md:block' : ''
                      }
                    >
                      {index === breadcrumbs.length - 1 ? (
                        <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink
                          render={<Link to={crumb.href as any} />}
                        >
                          {crumb.label}
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {index < breadcrumbs.length - 1 && (
                      <BreadcrumbSeparator className="hidden md:block" />
                    )}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  )
}
